<script setup lang="ts">
import { PriceTypeMode } from '@enums/priceTypeMode';
import { computed } from 'vue';
import IconPriceDropped from '@components/OfferCard/icons/IconPriceDropped.vue';
import Typography from '@components/Typography.vue';
import { trans } from '@tools/translation';
import { currencyNoDecimal } from '@tools/number-formatter';
import type { Color } from '@global-types/colors.ts';
import { OfficeOfferPriceType } from '@enums/officeOfferPriceType.ts';

const {
  offer,
  price,
  suffix = PriceTypeMode.SHORT,
  color,
} = defineProps<{
  offer: {
    is_price_drop_recent: boolean,
    price_type: OfficeOfferPriceType,
    office_type: { code: string } | null
  },
  price: number,
  suffix?: PriceTypeMode,
  color?: Color,
}>();

const isPriceDropRecent = computed<boolean>(() => offer.is_price_drop_recent);

const priceColor = computed<Color>(() => {
  if (color) {
    return color;
  }

  return isPriceDropRecent.value ? 'green' : 'black';
});

const priceTypeLabel = computed<string>(() => {
  if (offer.price_type === OfficeOfferPriceType.RENT_WORKSTATION) {
    return trans(`price.workstation.${suffix}`);
  }

  return trans(`price.monthly.${suffix}`);
});
</script>

<template>
  <div class="offer-price">
    <Typography
      class="offer-price__amount"
      variant="content-heading-2"
      :color="priceColor"
    >
      {{ currencyNoDecimal(price) }}

      <IconPriceDropped v-if="isPriceDropRecent" />
    </Typography>

    <Typography
      variant="body-small"
      :color="color ?? 'gray-darker'"
    >
      {{ trans('global.price_tax') }} {{ priceTypeLabel }}
    </Typography>
  </div>
</template>

<style scoped lang="scss">
@use '@styles/variables' as *;
@use '@styles/mixins' as *;

.offer-price {
  display: flex;
  gap: toRem(8);
  align-items: center;
  height: auto;
  white-space: nowrap;

  &__amount {
    display: flex;
    align-items: center;
    gap: toRem(4);
  }
}
</style>
